import { NavatticServiceError } from './errors'
import { type NavatticPopupOptions } from './types'

export const NAVATTIC_EMBEDS_SCRIPT_ID = 'navattic-embeds-script'
export const NAVATTIC_EMBEDS_SCRIPT_URL = 'https://js.navattic.com/embeds.js'

export const injectNavatticEmbeds = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.NavatticEmbed) {
      resolve()
      return
    }

    // Remove the failed script so a later call can re-inject a fresh one instead
    // of finding a dead tag whose load/error events will never fire again.
    const rejectOnError = (element: Element, message: string) => {
      element.remove()
      reject(new NavatticServiceError(message))
    }

    const onLoad = (element: Element) => () => {
      if (!window.NavatticEmbed) {
        rejectOnError(element, 'Navattic embeds library is not available')
        return
      }

      resolve()
    }

    const onError = (element: Element) => () =>
      rejectOnError(element, 'Failed to load Navattic embeds.js')

    const existingScript = document.getElementById(NAVATTIC_EMBEDS_SCRIPT_ID)
    if (existingScript) {
      existingScript.addEventListener('load', onLoad(existingScript), {
        once: true,
      })
      existingScript.addEventListener('error', onError(existingScript), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.id = NAVATTIC_EMBEDS_SCRIPT_ID
    script.src = NAVATTIC_EMBEDS_SCRIPT_URL
    script.async = true
    script.addEventListener('load', onLoad(script), { once: true })
    script.addEventListener('error', onError(script), { once: true })
    document.head.appendChild(script)
  })

export class NavatticService {
  /**
   * Opens a Navattic tour in Navattic's own pop-up overlay.
   * https://docs.navattic.com/share/embed#pop-up-embed
   */
  async openTour(
    url: string,
    options: NavatticPopupOptions = {}
  ): Promise<void> {
    if (!url) {
      throw new NavatticServiceError('No Navattic tour URL was provided')
    }

    try {
      await injectNavatticEmbeds()
    } catch (error) {
      if (error instanceof NavatticServiceError) {
        throw error
      }

      throw new NavatticServiceError(
        'Failed to load Navattic embeds.js',
        error as Error
      )
    }

    const embed = window.NavatticEmbed

    if (!embed) {
      throw new NavatticServiceError('Navattic embeds library is not available')
    }

    embed.initPopup(url, { preload: true, ...options })
    embed.openPopup(url)
  }
}
